import { Box, Grid } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import { StyledHeading } from '../styles/styles';
import {
  PLACEHOLDER_ROLE_OPTIONS,
  REPRESENTATIVE_TYPE_OPTIONS,
} from './placeholderContactAdminUtils';
import { PlaceholderContactEditFormFields } from './placeholderContactAdminTypes';

interface PlaceholderContactEditFormProps {
  tone?: keyof typeof Palette;
  editFormFields: PlaceholderContactEditFormFields;
  validationErrors: Record<string, string>;
  submitting: boolean;
  onFieldChange: (field: keyof PlaceholderContactEditFormFields, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

function renderTextField(
  name: keyof PlaceholderContactEditFormFields,
  label: string,
  editFormFields: PlaceholderContactEditFormFields,
  validationErrors: Record<string, string>,
  onFieldChange: PlaceholderContactEditFormProps['onFieldChange'],
  inputProps?: Partial<React.ComponentProps<typeof StyledInput>>,
) {
  const value = editFormFields[name] || '';
  const error = validationErrors[name];

  return (
    <StyledInput
      label={label}
      value={value}
      onChange={(event) => onFieldChange(name, event.target.value)}
      error={!!error}
      helperText={error}
      fullWidth
      {...inputProps}
    />
  );
}

function renderSelectField(
  name: keyof PlaceholderContactEditFormFields,
  label: string,
  options: { value: string; label: string }[],
  editFormFields: PlaceholderContactEditFormFields,
  validationErrors: Record<string, string>,
  onFieldChange: PlaceholderContactEditFormProps['onFieldChange'],
) {
  const value = editFormFields[name] || '';
  const error = validationErrors[name];

  return (
    <StyledInput
      select
      label={label}
      value={value}
      onChange={(event) => onFieldChange(name, event.target.value)}
      error={!!error}
      helperText={error}
      fullWidth
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </StyledInput>
  );
}

const PlaceholderContactEditForm: React.FC<PlaceholderContactEditFormProps> = ({
  editFormFields,
  validationErrors,
  submitting,
  onFieldChange,
  onSave,
  onCancel,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <StyledHeading headerVariant="heading2" sx={{ mb: 3 }}>
      Edit Placeholder Contact
    </StyledHeading>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        {renderTextField(
          'firstName',
          'First Name',
          editFormFields,
          validationErrors,
          onFieldChange,
          {
            required: true,
          },
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderTextField('lastName', 'Last Name', editFormFields, validationErrors, onFieldChange, {
          required: true,
        })}
      </Grid>
      <Grid item xs={12}>
        {renderTextField('email', 'Email', editFormFields, validationErrors, onFieldChange, {
          required: true,
          type: 'email',
        })}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderSelectField(
          'role',
          'Role',
          PLACEHOLDER_ROLE_OPTIONS,
          editFormFields,
          validationErrors,
          onFieldChange,
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {editFormFields.role === 'REPRESENTATIVE' &&
          renderSelectField(
            'representativeType',
            'Representative Type',
            REPRESENTATIVE_TYPE_OPTIONS,
            editFormFields,
            validationErrors,
            onFieldChange,
          )}
      </Grid>
      {editFormFields.representativeType === 'OTHER' && (
        <Grid item xs={12}>
          {renderTextField(
            'otherRepresentativeType',
            'Other Representative Type',
            editFormFields,
            validationErrors,
            onFieldChange,
          )}
        </Grid>
      )}
    </Grid>

    <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
      <StyledButton variant="contained" onClick={onSave} disabled={submitting}>
        {submitting ? 'Saving...' : 'Save Changes'}
      </StyledButton>
      <StyledButton variant="outlined" onClick={onCancel} disabled={submitting}>
        Cancel
      </StyledButton>
    </Box>
  </Box>
);

export default PlaceholderContactEditForm;
